// Restroom calculations shared by the facilities page, its charts, the details modal and the Overview.
// The v2 daily route returns, per restroomId, running entrance/exit counts summed across sensors
// ("countIn"/"countOut", one point per minute) and queue readings, with venue-local timestamps read
// the same way as gates (times after midnight stay on the game day). A visit is an entrance.
//
// There is no source for cleaning events yet, so cleaning is modelled from visits alone: a restroom
// is due a cleaning every CLEANING_THRESHOLD visits, and is taken as cleaned when it gets there
// (the count toward the next cleaning starts again from zero).
import {
  timestampMinute, clockToMinute, minuteToClock, nowMinute, queueSeries, latestQueue, peakQueue, queueColor, isLongQueue, QUEUE_TARGET,
} from '../Gates/gatesMetrics';

export { clockToMinute, minuteToClock, nowMinute, queueColor, isLongQueue, QUEUE_TARGET };

export const CLEANING_THRESHOLD = 200;
// "Due soon" from this many entries toward the next cleaning (75%)
export const DUE_SOON_AT = 150;

// Visits per minute are averaged over this many minutes for the live rate and the "due in" estimate
const RATE_MINUTES = 10;

const TYPE_LABELS = { mens: "Men's", womens: "Women's", family: 'Family' };
const TYPE_ICONS = { mens: 'gender-male', womens: 'gender-female', family: 'people-fill' };

// The config has no type field, so it's read from the label ("Mens Restroom", "Women's", "Family Restroom").
// Women's is checked first because "women" contains "men".
export const restroomType = (label) => {
  const text = String(label || '').toLowerCase();
  if (/wom[ae]n|ladies|female/.test(text)) return 'womens';
  if (/family|companion|all[\s-]?gender|unisex|accessible/.test(text)) return 'family';
  if (/\bmen|\bmens|\bmale|gentlemen/.test(text)) return 'mens';
  return 'other';
};
export const typeLabel = (type) => TYPE_LABELS[type] || 'Restroom';
export const typeIcon = (type) => TYPE_ICONS[type] || 'person';

// Config restrooms ([{ restroomId, sectionId, label, imageURL }]) + v2 daily response
// ({ restroomId: { totalEntrances, totalExits, data: [{ timestamp, countIn, countOut }], queueData } })
// -> one restroom each, in config order. A label used by more than one restroom gets its section added.
export const transformRestrooms = (data, configRestrooms = []) => {
  const labelCounts = {};
  configRestrooms.forEach((r) => {
    const key = String(r.label || '').trim().toLowerCase();
    labelCounts[key] = (labelCounts[key] || 0) + 1;
  });
  return configRestrooms.map((r) => {
    const key = String(r.restroomId);
    const d = data?.[key] || {};
    const label = String(r.label || '').trim() || `Restroom ${r.restroomId}`;
    const duplicate = labelCounts[label.toLowerCase()] > 1;
    const type = restroomType(label);
    return {
      id: key,
      restroomId: r.restroomId,
      sectionId: r.sectionId != null ? String(r.sectionId) : '',
      type,
      typeLabel: typeLabel(type),
      imageURL: r.imageURL || null,
      name: duplicate && r.sectionId != null ? `${label}, Section ${r.sectionId}` : label,
      area: r.sectionId != null ? `Section ${r.sectionId}` : '',
      hasQueueSensors: !!r.multiSensor?.queue_time,
      totalCount: Number(d.totalEntrances) || 0,
      totalExits: Number(d.totalExits) || 0,
      rawData: d.data || [],
      queueData: d.queueData || [],
    };
  });
};

// Running visit count, one point per minute (when a minute has more than one reading, the later one wins)
export const visitSeries = (restroom, dateKey) => {
  const byMinute = new Map();
  [...(restroom.rawData || [])]
    .filter((item) => item && item.timestamp)
    .sort((a, b) => (a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0))
    .forEach((item) => {
      const minute = timestampMinute(item.timestamp, dateKey);
      if (minute != null) byMinute.set(minute, Number(item.countIn ?? item.count) || 0);
    });
  return Array.from(byMinute.entries())
    .map(([minute, count]) => ({ minute, count }))
    .sort((a, b) => a.minute - b.minute);
};

// Sum of every restroom's running count, carrying each restroom's last reading forward
export const combinedVisitSeries = (seriesList) => {
  const minutes = Array.from(new Set(seriesList.flatMap((s) => s.map((p) => p.minute)))).sort((a, b) => a - b);
  const cursors = seriesList.map(() => 0);
  const last = seriesList.map(() => 0);
  return minutes.map((minute) => {
    seriesList.forEach((series, i) => {
      while (cursors[i] < series.length && series[cursors[i]].minute <= minute) {
        last[i] = series[cursors[i]].count;
        cursors[i] += 1;
      }
    });
    return { minute, count: last.reduce((sum, c) => sum + c, 0) };
  });
};

// Running count at a minute (the last reading at or before it; zero before the first)
export const countAt = (series, minute) => {
  let value = 0;
  for (let i = 0; i < series.length && series[i].minute <= minute; i += 1) value = series[i].count;
  return value;
};

// Visits within each interval of `size` minutes. Counting starts from zero at the start of the day;
// a counter reset never shows as negative.
export const visitFlow = (series, size) => {
  if (!series.length) return [];
  const first = Math.floor(series[0].minute / size) * size;
  const lastMinute = series[series.length - 1].minute;
  const buckets = [];
  let prev = 0;
  let i = 0;
  for (let start = first; start <= lastMinute; start += size) {
    let end = prev;
    while (i < series.length && series[i].minute < start + size) {
      end = series[i].count;
      i += 1;
    }
    buckets.push({ start, end: start + size, visits: Math.max(0, end - prev) });
    prev = end;
  }
  return buckets;
};

// Busiest quarter-hour, on fixed slots (6:00, 6:15, ...) so times agree across the page
export const peakQuarter = (flow, len = 15) => {
  const slots = new Map();
  flow.forEach((b) => {
    const start = Math.floor(b.start / len) * len;
    slots.set(start, (slots.get(start) || 0) + b.visits);
  });
  let best = null;
  slots.forEach((total, start) => {
    if (!best || total > best.total) best = { start, end: start + len, total };
  });
  return best && best.total > 0 ? best : null;
};

// Visits per minute over the last `window` minutes of readings (by time, not by number of readings)
export const recentRate = (series, window = RATE_MINUTES) => {
  if (series.length < 2) return null;
  const latest = series[series.length - 1];
  let base = null;
  for (let i = series.length - 2; i >= 0; i -= 1) {
    base = series[i];
    if (latest.minute - series[i].minute >= window) break;
  }
  const minutes = latest.minute - base.minute;
  if (minutes <= 0) return null;
  return Math.max(0, (latest.count - base.count) / minutes);
};

// Where a restroom is in its cleaning cycle after `visits` visits
export const cleaningCycle = (visits) => {
  const total = Math.max(0, visits || 0);
  const since = total % CLEANING_THRESHOLD;
  return {
    since,
    toGo: CLEANING_THRESHOLD - since,
    due: Math.floor(total / CLEANING_THRESHOLD),
    progress: since / CLEANING_THRESHOLD,
    dueSoon: since >= DUE_SOON_AT,
  };
};

// Minutes of the day when the running count reached each multiple of the threshold.
// `from` (a minute) counts visits from then on, for an analysis window.
export const dueTimes = (series, from = null, to = null) => {
  const base = from != null ? countAt(series, from) : 0;
  const times = [];
  let next = CLEANING_THRESHOLD;
  series.forEach((p) => {
    if (from != null && p.minute <= from) return;
    if (to != null && p.minute > to) return;
    while (p.count - base >= next) {
      times.push({ n: times.length + 1, minute: p.minute, at: next });
      next += CLEANING_THRESHOLD;
    }
  });
  return times;
};

// Status for colours and sorting: 'soon' (due soon) or 'ok'; null with no readings
export const cycleColor = (cycle) => (cycle && cycle.dueSoon ? 'var(--q-warn)' : 'var(--q-good)');

// Everything the page needs for one restroom, worked out once.
// `window` ({ start, end } in game-day minutes) limits the numbers to that range;
// without one, the day's totals are used.
export const buildRestroomStats = (restroom, dateKey, liveNow, window = null) => {
  const series = visitSeries(restroom, dateKey);
  const dayTotal = series.length ? series[series.length - 1].count : restroom.totalCount || 0;
  const visits = window
    ? Math.max(0, countAt(series, window.end) - countAt(series, window.start))
    : dayTotal;
  const inWindow = (m) => !window || (m >= window.start && m < window.end);
  const flow = visitFlow(series, 1).filter((b) => inWindow(b.start));
  const due = window ? dueTimes(series, window.start, window.end) : dueTimes(series);
  // Live: the cycle runs on the whole day so far
  const cycle = cleaningCycle(dayTotal);
  const rate = liveNow != null ? recentRate(series) : null;
  const latest = series.length ? series[series.length - 1].minute : null;
  const queue = queueSeries(restroom, dateKey);
  const queueInWindow = window ? queue.filter((p) => p.minute >= window.start && p.minute <= window.end) : queue;
  return {
    restroom,
    id: restroom.id,
    label: restroom.name,
    series,
    visits,
    dueCount: due.length,
    dueTimes: due,
    peak: peakQuarter(flow),
    cycle,
    rate,
    // Minutes until the next cleaning at the current rate (live only, and only while visits are coming in)
    dueIn: rate && rate > 0.05 ? Math.round(cycle.toGo / rate) : null,
    stale: liveNow != null && latest != null && liveNow - latest > 15,
    queue,
    hasQueue: queue.length > 0,
    queueNow: liveNow != null ? latestQueue(queue, liveNow) : null,
    queuePeak: peakQueue(queueInWindow),
    longMinutes: queueInWindow.filter((p) => isLongQueue(p.queueTime)).length,
  };
};

// "~25 min", "~1h 10m"
export const formatDueIn = (minutes) => {
  if (minutes == null) return null;
  if (minutes < 1) return 'now';
  if (minutes < 60) return `~${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `~${h}h ${m}m` : `~${h}h`;
};

export const formatRate = (rate) => (rate == null ? '—' : rate >= 10 ? Math.round(rate).toLocaleString() : rate.toFixed(1));
